import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import {
  MEMPHIS_ORG_ACTION_DESCRIPTION,
  MEMPHIS_RESPONSE_CHECKLIST_LABELS,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import {
  keys as orgActionKeys,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';

/** Shared locks so React Strict Mode remounts do not double-create. */
const ensureLocks = new Map<string, Promise<unknown>>();

async function ensureMemphisThreatOrgActionsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<GetOrgActionResponse[]>(
    `${config.apiGateway.ics_org_actions}/${groupGuid}`
  );
  const existingNames = new Set(
    (resp.data ?? [])
      .filter((action) => !action.archived)
      .map((action) => action.name)
  );

  const missing = MEMPHIS_RESPONSE_CHECKLIST_LABELS.filter(
    (label) => !existingNames.has(label)
  );
  if (missing.length === 0) {
    return;
  }

  await Promise.all(
    missing.map((name) =>
      axios.post(`${config.apiGateway.ics_org_actions}/${groupGuid}`, {
        name,
        description: MEMPHIS_ORG_ACTION_DESCRIPTION,
        checklist: [],
        status: 'Planned',
        user_guid: userGuid,
        utc_offset: -new Date().getTimezoneOffset(),
      })
    )
  );

  await queryClient.invalidateQueries({
    queryKey: orgActionKeys.byGroupAndUser(groupGuid, userGuid),
  });
}

/**
 * When CFA is on, ensure the four Memphis Response Checklist labels exist as
 * Planned / Unassigned (no poc) org actions for the current hub.
 */
export function useEnsureMemphisThreatOrgActions() {
  const cfaEnabled = useCfaDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cfaEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureMemphisThreatOrgActionsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[CFA Memphis] Failed to ensure Response Checklist org actions',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [cfaEnabled, groupGuid, userGuid, queryClient]);
}
